import { Label, TableRowCreationEvent, TableContentsChangedEvent, LookupModelSelectionEvent, TableRow, Textbox, DataSourceExecutionEvent, DataSourceAction, ClickEvent, BlurEvent } from "@mcleod/components";
import { AutogenLayoutChargeCodeXref } from "./autogen/AutogenLayoutChargeCodeXref";
import { getFilteredRowCount, updateXrefLabelFormatting, toggleExpandablePanel, onBlurCrossReference } from "./DataFusionUtil";

export class ChargeCodeXref extends AutogenLayoutChargeCodeXref {
    private _panelExpanded: boolean = false;
    private labelCrossRefCount: Label;
    private _mainLabelTitle: string = "Charge Code";
    override async onLoad() {
        await super.onLoad();

        // Create and add the count label next to the main label
        this.createCountLabel();

        // Set initial label formatting and collapse panels by default
        this.updateLabelFormatting(true);
    }

    private createCountLabel() {
        // Create the count label
        this.labelCrossRefCount = new Label({
            text: "",
            color: "primary",
            fontBold: true,
            rowBreak: false,
            marginBottom: 0,
            paddingBottom: 0,
            fontSize: "large"
        });

        // Find the parent container of the main label
        const labelParent = this.labelCrossRef.parent;
        if (labelParent && this.horizontalspacerForChargeCodeEdi) {
            // Insert the count label between the main label and the horizontal spacer
            const spacerIndex = labelParent.indexOf(this.horizontalspacerForChargeCodeEdi);
            if (spacerIndex >= 0) {
                labelParent.insert(this.labelCrossRefCount, spacerIndex);
            } else {
                // Fallback: add after the main label
                const labelIndex = labelParent.indexOf(this.labelCrossRef);
                if (labelIndex >= 0) {
                    labelParent.insert(this.labelCrossRefCount, labelIndex + 1);
                }
            }
        }
    }

    /** This is an event handler for the onRowCreate event of tableCrossRef. */
    tableCrossRefOnRowCreate(event: TableRowCreationEvent) {
        console.log("Row create event triggered");

        if (this.owner && this.owner.mainDataSource && this.owner.mainDataSource.activeRow) {
            const parentRow = this.owner.mainDataSource.activeRow;
            const newRow = event.getTableRow().data;

            // Reset original data FIRST, before setting values, to prevent marking datasource as changed
            newRow.resetOriginalData();

            // Set the required fields from parent
            newRow.set("partner_id", parentRow.get("partner_id"));
            newRow.set("version", parentRow.get("version"));

            // Reset again after setting to clear the dirty flag
            newRow.resetOriginalData();

            // Force the datasource to recalculate its hasChanged state
            this.sourceChargeCodeEdi?.notifyHasChangedComponents(false);
        }

        // Update label formatting and panel state after row creation
        setTimeout(() => {
            this.updateLabelFormatting();
        }, 10);
    }
    private updateLabelFormatting(forceCollapse: boolean = false) {
        const rowCount = getFilteredRowCount(this.tableCrossRef);

        // Set the main label text without count
        this.labelCrossRef.text = this._mainLabelTitle;

        // Set the count label text and visibility
        if (this.labelCrossRefCount) {
            this.labelCrossRefCount.text = rowCount > 0 ? rowCount.toString() : "";
            this.labelCrossRefCount.visible = rowCount > 0;
        }

        updateXrefLabelFormatting([
            {
                table: this.tableCrossRef,
                label: this.labelCrossRef,
                isExpanded: this._panelExpanded,
                collapsiblePanel: this.panelCollapsible,
                expandButton: this.buttonExpand,
                onStateChange: (newState: boolean) => {
                    this._panelExpanded = newState;
                }
            }
        ], forceCollapse);
    }

    /** This is an event handler for the onContentsChanged event of tableCrossRef. */
    tableCrossRefOnContentsChanged(event: TableContentsChangedEvent) {
        // Update label formatting when table contents change (add/delete rows)
        setTimeout(() => this.updateLabelFormatting(), 10);
    }

    /** This is an event handler for the onLookupModelSelection event of textBoxRevenueCode. */
    onLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const selectedRow = event.selection;
            const tableRow = TableRow.getContainingTableRow(event.target);

            if (tableRow) {
                // Set the charge_code descr field when a new charge code is selected
                tableRow.data.set("charge_code_descr", selectedRow.get("descr"));

                // Find and update the description textbox
                const textboxDescription = tableRow.findComponentById("textboxDescription") as Textbox;
                if (textboxDescription) {
                    textboxDescription.text = selectedRow.get("descr");
                }
            }
        }
    }

    /** This is an event handler for the afterExecution event of sourceEdiCrossRefRevenueCode. */
    afterExecution(event: DataSourceExecutionEvent) {
        // Only apply formatting after search operations and when we have data
        if (event.getAction() === DataSourceAction.SEARCH) {
            // Use setTimeout to ensure the formatting happens after the data is fully loaded
            setTimeout(() => {
                this.updateLabelFormatting();
            }, 100);
        }
    }

    /** This is an event handler for the onClick event of buttonExpand. */
    async buttonExpandOnClick(event: ClickEvent) {
        this._panelExpanded = await toggleExpandablePanel(
            this._panelExpanded,
            this.panelCollapsible,
            this.buttonExpand
        );
    }

    async onBlurChargeCode(event: BlurEvent) {
        await onBlurCrossReference(event, this.tableCrossRef, "edi_charge_code");
    }
}
